/**
 * RegisterPage — citizen sign-up.
 *
 * Step 2 ("Create Account") posts to `POST /v1/auth/register` on
 * service-identity via `registerCustomer`. Service-identity creates the
 * user_account, hardcodes the customer-role grant, calls service-customer's
 * m2m `POST /v1/customers/internal` to materialise the Customer profile, and
 * returns access + refresh tokens. We persist both tokens via `tokenStore` so
 * the user lands authenticated and Step 3 is the dashboard CTA.
 *
 * Step 1 (phone OTP) is still a UI placeholder — phone verification delivery
 * (SMS) is a deferred follow-up ticket. The countdown timers fake the
 * resend window. The dev banner notes the gap.
 */
import { type ChangeEvent, type FormEvent, type JSX, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import {
  Phone,
  User,
  Mail,
  Lock,
  Building2,
  CheckCircle2,
  Circle,
  XCircle,
  Loader2,
  Eye,
  EyeOff,
  KeyRound,
} from 'lucide-react';

import { ApiError } from '../../lib/api-client';
import {
  checkAvailability,
  requestEmailVerification,
  verifyEmailVerification,
} from '../../lib/api/auth';
import { registerCustomer } from '../../lib/api/customer';
import {
  setCustomerToken,
  setCustomerRefreshToken,
} from '../../lib/auth/tokenStore';
import { env } from '../../lib/env';
import { OtpCountdown } from './OtpCountdown';

interface StepIndicatorProps {
  readonly currentStep: number;
  readonly verifyVia: 'phone' | 'email';
}

function StepIndicator({ currentStep, verifyVia }: StepIndicatorProps): JSX.Element {
  const step1Label = verifyVia === 'email' ? 'Email Verification' : 'Phone Verification';
  const steps: readonly string[] = [step1Label, 'Personal Details', 'Confirmation'];
  return (
    <div className="flex items-center justify-between mb-8">
      {steps.map((label, i) => {
        const step = i + 1;
        const isActive = step === currentStep;
        const isComplete = step < currentStep;
        return (
          <div key={label} className="flex items-center flex-1">
            <div className="flex flex-col items-center flex-1">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                  isComplete || isActive ? 'bg-nipost-green text-white' : 'bg-gray-200 text-gray-500'
                }`}
              >
                {isComplete ? <CheckCircle2 className="w-5 h-5" /> : step}
              </div>
              <span className={`text-xs mt-1.5 ${isActive || isComplete ? 'text-nipost-green font-medium' : 'text-muted'}`}>
                {label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div className={`h-0.5 w-full -mt-5 mx-1 ${step < currentStep ? 'bg-nipost-green' : 'bg-gray-200'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Mirrors `DEFAULT_PASSWORD_POLICY` in
 *  `libs/service-base/src/auth/password-policy.service.ts`. The order matters:
 *  it's the order shown to the user on the form, top-to-bottom. */
const PASSWORD_RULES: readonly { label: string; test: (pw: string) => boolean }[] = [
  { label: 'At least 12 characters', test: (pw) => pw.length >= 12 },
  { label: 'One uppercase letter (A–Z)', test: (pw) => /\p{Lu}/u.test(pw) },
  { label: 'One number (0–9)', test: (pw) => /\p{Nd}/u.test(pw) },
  { label: 'One symbol (!@#$ etc.)', test: (pw) => /[^A-Za-z0-9]/.test(pw) },
];

interface PasswordPolicyChecklistProps {
  readonly password: string;
}

function PasswordPolicyChecklist({ password }: PasswordPolicyChecklistProps): JSX.Element {
  return (
    <ul className="mt-2 space-y-1" aria-label="Password requirements">
      {PASSWORD_RULES.map((rule) => {
        const met = rule.test(password);
        // Three states: untyped (neutral grey), typed-but-failing (red),
        // typed-and-passing (green). Using `password.length > 0` to avoid
        // surfacing red rules before the user starts typing.
        const tone = password.length === 0
          ? 'text-gray-400'
          : met
          ? 'text-green-600'
          : 'text-red-500';
        const Icon = met ? CheckCircle2 : password.length === 0 ? Circle : XCircle;
        return (
          <li key={rule.label} className={`flex items-center gap-2 text-xs ${tone}`}>
            <Icon className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
            <span>{rule.label}</span>
          </li>
        );
      })}
    </ul>
  );
}

export function RegisterPage(): JSX.Element {
  const navigate = useNavigate();
  // Verification channel — phone SMS OTP (existing) or email link/code (new).
  // The toggle sits at the top of Step 1; whichever is chosen drives the
  // sub-form below. Both paths land the user at Step 2 with one identifier
  // already verified; the other is still required at register time.
  const [verifyVia, setVerifyVia] = useState<'phone' | 'email'>('phone');
  const [step, setStep] = useState<number>(1);

  // Step 1
  const [phone, setPhone] = useState<string>('');
  const [otpSent, setOtpSent] = useState<boolean>(false);
  const [otp, setOtp] = useState<string>('');
  const [phoneVerified, setPhoneVerified] = useState<boolean>(false);
  const [sendingOtp, setSendingOtp] = useState<boolean>(false);
  const [verifyingOtp, setVerifyingOtp] = useState<boolean>(false);

  // Step 2
  const [fullName, setFullName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const [accountType, setAccountType] = useState<'individual' | 'business'>('individual');
  const [businessName, setBusinessName] = useState<string>('');
  const [cacNumber, setCacNumber] = useState<string>('');
  const [step2Touched, setStep2Touched] = useState<boolean>(false);
  const [agreedTerms, setAgreedTerms] = useState<boolean>(false);

  const phoneValid = /^\d{10,11}$/.test(phone);
  const otpValid = /^\d{6}$/.test(otp);
  const passwordsMatch = password === confirmPassword && password.length > 0;
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  // Mirrors service-identity's DEFAULT_PASSWORD_POLICY: 12+ chars with at
  // least one uppercase letter, one digit, and one symbol. We pre-validate
  // client-side so the user gets immediate feedback instead of a confusing
  // round-trip 400 from the server.
  const passwordPolicyOk =
    password.length >= 12 &&
    /\p{Lu}/u.test(password) &&
    /\p{Nd}/u.test(password) &&
    /[^A-Za-z0-9]/.test(password);
  const step2Valid =
    fullName.trim().length >= 2 &&
    emailValid &&
    passwordPolicyOk &&
    passwordsMatch &&
    agreedTerms &&
    (accountType === 'individual' || (businessName.trim() && cacNumber.trim()));

  const registerMutation = useMutation({
    mutationFn: (body: Parameters<typeof registerCustomer>[0]) => registerCustomer(body),
    onSuccess: (result) => {
      // Persist tokens so the user lands authenticated. The dashboard CTA on
      // step 3 then routes into a real session.
      setCustomerToken(result.accessToken);
      setCustomerRefreshToken(result.refreshToken);
      setStep(3);
    },
  });

  // Surfaces "this phone is already registered" before we kick off the OTP
  // charade. Cleared whenever the user edits the phone field.
  const [phoneTakenError, setPhoneTakenError] = useState<string>('');

  // ── Email-verification path (verifyVia === 'email') ────────────────────────
  const [verifyEmail, setVerifyEmail] = useState<string>('');
  const [emailCodeSent, setEmailCodeSent] = useState<boolean>(false);
  const [emailCode, setEmailCode] = useState<string>('');
  const [emailVerified, setEmailVerified] = useState<boolean>(false);
  const [sendingEmailCode, setSendingEmailCode] = useState<boolean>(false);
  const [verifyingEmailCode, setVerifyingEmailCode] = useState<boolean>(false);
  const [emailVerifyError, setEmailVerifyError] = useState<string>('');
  const [emailTakenError, setEmailTakenError] = useState<string>('');
  /** Set to true when the backend returns 404 on /email-verification/request,
   *  meaning the endpoint isn't deployed yet. We flip the toggle back to
   *  phone and show a one-line notice. */
  const [emailRolloutNotice, setEmailRolloutNotice] = useState<boolean>(false);
  const verifyEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(verifyEmail.trim());
  const emailCodeValid = /^\d{6}$/.test(emailCode);

  async function handleSendEmailCode(): Promise<void> {
    if (!verifyEmailValid) return;
    setEmailTakenError('');
    setEmailVerifyError('');
    setSendingEmailCode(true);
    try {
      const availability = await checkAvailability({ email: verifyEmail.trim() }).catch(
        () => ({ emailTaken: false, phoneTaken: false }),
      );
      if (availability.emailTaken) {
        setEmailTakenError('This email is already registered. Sign in instead?');
        return;
      }
      const res = await requestEmailVerification(verifyEmail.trim());
      setEmailCodeSent(true);
      // Dev shortcut: backend echoes the code when
      // IDENTITY_AUTOVERIFY_REGISTRATION=true so testing is frictionless.
      if (res.devCode) setEmailCode(res.devCode);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        // Endpoint isn't deployed in this environment — fall back gracefully.
        setEmailRolloutNotice(true);
        setVerifyVia('phone');
        return;
      }
      setEmailVerifyError(
        err instanceof Error
          ? err.message
          : 'Could not send the verification code. Please try again.',
      );
    } finally {
      setSendingEmailCode(false);
    }
  }

  async function handleVerifyEmailCode(): Promise<void> {
    if (!emailCodeValid) return;
    setEmailVerifyError('');
    setVerifyingEmailCode(true);
    try {
      await verifyEmailVerification(verifyEmail.trim(), emailCode);
      setEmailVerified(true);
      // Inherit the verified email into the Step-2 email field so the user
      // doesn't retype it. They can still edit on Step 2 if they need to.
      setEmail(verifyEmail.trim());
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { code?: string } | undefined;
        const code = body?.code;
        if (code === 'INVALID_CODE') setEmailVerifyError('That code is wrong. Try again.');
        else if (code === 'TOKEN_EXPIRED') setEmailVerifyError('Code expired — request a new one.');
        else if (code === 'TOKEN_CONSUMED') setEmailVerifyError('That code was already used.');
        else setEmailVerifyError(err.message);
      } else {
        setEmailVerifyError(err instanceof Error ? err.message : 'Verification failed.');
      }
    } finally {
      setVerifyingEmailCode(false);
    }
  }

  async function handleSendOtp(): Promise<void> {
    if (!phoneValid) return;
    setPhoneTakenError('');
    setSendingOtp(true);
    try {
      const e164 = phone.startsWith('0') ? `+234${phone.slice(1)}` : `+234${phone}`;
      const res = await fetch(
        `${env.identityBaseUrl}/v1/auth/check-availability?phone=${encodeURIComponent(e164)}`,
        { headers: { accept: 'application/json' } },
      );
      if (res.ok) {
        const body = (await res.json()) as { phoneTaken?: boolean };
        if (body.phoneTaken) {
          setPhoneTakenError('This phone number is already registered. Sign in instead?');
          return;
        }
      }
      // If the check fails (network / 5xx), fall through and let the user
      // continue — the register endpoint is the authoritative duplicate
      // gate and will surface a friendly 409 message.
      setOtpSent(true);
    } finally {
      setSendingOtp(false);
    }
  }

  function handleVerifyOtp(): void {
    if (!otpValid) return;
    setVerifyingOtp(true);
    window.setTimeout(() => {
      setVerifyingOtp(false);
      setPhoneVerified(true);
    }, 500);
  }

  function handleStep2Submit(e: FormEvent): void {
    e.preventDefault();
    setStep2Touched(true);
    if (!step2Valid) return;
    if (!email.trim()) {
      // Email is required by service-identity (it's the unique login key).
      // The form already requires it indirectly via passwordsMatch; this is
      // belt-and-braces.
      return;
    }

    // Normalise NG locals to E.164. service-identity's RegisterSchema accepts
    // either, but we send the canonical form so logs are consistent.
    const e164 = phone.startsWith('0') ? `+234${phone.slice(1)}` : `+234${phone}`;

    registerMutation.mutate({
      fullName: fullName.trim(),
      primaryPhone: e164,
      primaryEmail: email.trim(),
      password,
      ...(accountType === 'business' && businessName.trim()
        ? { displayName: businessName.trim() }
        : {}),
    });
  }

  const submitError = registerMutation.error;
  const submitErrorMessage = (() => {
    if (!submitError) return null;
    if (submitError instanceof ApiError) {
      if (submitError.status === 409) {
        const body = submitError.body as { code?: string; message?: string } | undefined;
        const code = body?.code ?? body?.message;
        if (code === 'PHONE_ALREADY_REGISTERED') {
          return 'An account with this phone number is already registered. Try signing in instead.';
        }
        if (code === 'EMAIL_ALREADY_REGISTERED' || code === undefined) {
          return 'An account with this email is already registered. Try signing in instead.';
        }
        return submitError.message;
      }
      if (submitError.status === 400) {
        // The server's PasswordPolicyService rejects with code
        // PASSWORD_POLICY_VIOLATION when the password fails any of:
        // length ≥ 12, uppercase, digit, or symbol. The frontend pre-validates
        // the same rules; if we get here the user typed a password that
        // looked OK but tripped a deeper rule (e.g. common-password list).
        return 'That password isn\'t strong enough — try at least 12 characters with a mix of upper/lowercase, a number, and a symbol. Avoid common words.';
      }
      if (submitError.status === 503) {
        return 'Registration is temporarily unavailable. Please try again in a moment.';
      }
      return submitError.message;
    }
    return submitError instanceof Error ? submitError.message : null;
  })();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-xl bg-nipost-green flex items-center justify-center mx-auto mb-4 shadow-lg">
            <span className="text-white font-bold text-2xl">N</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Create Account</h1>
          <p className="text-muted text-sm mt-1">Register for UPMP Customer Portal</p>
        </div>

        {/* Phone verification deferred notice — SMS delivery is not wired
            yet, so the OTP step in the form is a placeholder. The actual
            account creation happens against service-identity in step 2. */}
        <div className="mb-5 rounded-2xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3">
          <KeyRound className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
          <div className="text-xs text-amber-800">
            <p className="font-semibold text-amber-900 text-sm">Verify by phone or email</p>
            <p className="mt-0.5">
              Pick whichever channel you prefer below. SMS delivery is in preview — the
              phone OTP path accepts any 6-digit code in this build. Email verification
              is wired end-to-end against service-identity.
            </p>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-border p-8">
          <StepIndicator currentStep={step} verifyVia={verifyVia} />

          {/* Step 1: Verification (phone OR email) */}
          {step === 1 && (
            <div className="space-y-4">
              {/* Verification channel toggle. Visible until the user has
                  actually verified one side. */}
              {!phoneVerified && !emailVerified && (
                <fieldset>
                  <legend className="block text-sm font-medium text-gray-700 mb-2">
                    How should we verify your account?
                  </legend>
                  <div className="grid grid-cols-2 gap-2" role="radiogroup">
                    <label
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm cursor-pointer transition-colors ${
                        verifyVia === 'phone'
                          ? 'border-nipost-green bg-nipost-green/5 text-gray-900'
                          : 'border-border text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="verify-via"
                        value="phone"
                        checked={verifyVia === 'phone'}
                        onChange={() => setVerifyVia('phone')}
                        className="accent-nipost-green"
                      />
                      <Phone className="w-4 h-4" /> Phone (SMS)
                    </label>
                    <label
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm cursor-pointer transition-colors ${
                        verifyVia === 'email'
                          ? 'border-nipost-green bg-nipost-green/5 text-gray-900'
                          : 'border-border text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="verify-via"
                        value="email"
                        checked={verifyVia === 'email'}
                        onChange={() => {
                          setVerifyVia('email');
                          setEmailRolloutNotice(false);
                        }}
                        className="accent-nipost-green"
                      />
                      <Mail className="w-4 h-4" /> Email (code)
                    </label>
                  </div>
                  {emailRolloutNotice && (
                    <p className="mt-2 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1.5">
                      Email verification is rolling out shortly — please use Phone SMS for now.
                    </p>
                  )}
                </fieldset>
              )}

              {/* Email verification sub-form (verifyVia === 'email'). */}
              {verifyVia === 'email' && (
                <div className="space-y-4" data-testid="email-verify-block">
                  <div>
                    <label htmlFor="reg-verify-email" className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        id="reg-verify-email"
                        type="email"
                        value={verifyEmail}
                        onChange={(e) => {
                          setVerifyEmail(e.target.value);
                          setEmailTakenError('');
                          setEmailVerifyError('');
                        }}
                        placeholder="you@example.com"
                        disabled={emailCodeSent}
                        className={`w-full pl-10 pr-4 py-2.5 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green transition-colors disabled:bg-gray-50 ${
                          emailVerified ? 'border-green-400' : 'border-border'
                        }`}
                      />
                    </div>
                  </div>

                  {!emailCodeSent && emailTakenError && (
                    <div
                      className="px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-center justify-between gap-3"
                      role="alert"
                    >
                      <span>{emailTakenError}</span>
                      <Link
                        to={`/auth/login?email=${encodeURIComponent(verifyEmail)}`}
                        className="text-nipost-green hover:text-nipost-green-dark font-medium whitespace-nowrap"
                      >
                        Sign in
                      </Link>
                    </div>
                  )}

                  {!emailCodeSent && (
                    <button
                      type="button"
                      onClick={() => void handleSendEmailCode()}
                      disabled={!verifyEmailValid || sendingEmailCode || emailTakenError !== ''}
                      data-testid="email-verify-send"
                      className="w-full py-2.5 px-4 rounded-lg bg-nipost-green text-white font-medium text-sm hover:bg-nipost-green-dark transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {sendingEmailCode ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Sending…
                        </>
                      ) : (
                        'Send verification code'
                      )}
                    </button>
                  )}

                  {emailCodeSent && !emailVerified && (
                    <>
                      <div>
                        <label htmlFor="reg-email-code" className="block text-sm font-medium text-gray-700 mb-1.5">
                          Enter 6-digit code
                        </label>
                        <input
                          id="reg-email-code"
                          type="text"
                          value={emailCode}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => setEmailCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="000000"
                          maxLength={6}
                          data-testid="email-verify-code-input"
                          className="w-full px-4 py-2.5 rounded-lg border border-border text-sm text-center tracking-[0.3em] font-mono outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green"
                        />
                        <p className="text-xs text-muted mt-1">A verification code was sent to {verifyEmail}.</p>
                      </div>
                      {emailVerifyError && (
                        <p className="text-xs text-red-600">{emailVerifyError}</p>
                      )}
                      <button
                        type="button"
                        onClick={() => void handleVerifyEmailCode()}
                        disabled={!emailCodeValid || verifyingEmailCode}
                        data-testid="email-verify-submit"
                        className="w-full py-2.5 px-4 rounded-lg bg-nipost-green text-white font-medium text-sm hover:bg-nipost-green-dark transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {verifyingEmailCode ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Verifying…
                          </>
                        ) : (
                          'Verify code'
                        )}
                      </button>
                    </>
                  )}

                  {emailVerified && (
                    <>
                      <div className="flex items-center gap-2 text-green-600 bg-green-50 px-4 py-3 rounded-lg">
                        <CheckCircle2 className="w-5 h-5" />
                        <span className="text-sm font-medium">Email verified</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setStep(2)}
                        data-testid="email-verify-continue"
                        className="w-full py-2.5 px-4 rounded-lg bg-nipost-green text-white font-medium text-sm hover:bg-nipost-green-dark transition-colors"
                      >
                        Continue
                      </button>
                    </>
                  )}
                </div>
              )}

              {/* Phone verification sub-form (verifyVia === 'phone'). The
                  existing UI below is unchanged. */}
              {verifyVia === 'phone' && (
              <>
              <div>
                <label htmlFor="reg-phone" className="block text-sm font-medium text-gray-700 mb-1.5">Phone Number</label>
                <div className="relative flex">
                  <div className="flex items-center gap-1.5 px-3 py-2.5 bg-gray-50 border border-r-0 border-border rounded-l-lg text-sm text-gray-500">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <span>+234</span>
                  </div>
                  <input
                    id="reg-phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => {
                      setPhone(e.target.value.replace(/\D/g, ''));
                      setPhoneTakenError('');
                    }}
                    placeholder="8012345678"
                    maxLength={11}
                    disabled={otpSent}
                    className={`w-full px-4 py-2.5 rounded-r-lg border text-sm outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green transition-colors disabled:bg-gray-50 disabled:text-gray-500 ${
                      phoneVerified ? 'border-green-400' : 'border-border'
                    }`}
                  />
                </div>
              </div>

              {!otpSent && phoneTakenError && (
                <div
                  className="px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-center justify-between gap-3"
                  role="alert"
                >
                  <span>{phoneTakenError}</span>
                  <Link
                    to={`/auth/login?phone=${encodeURIComponent(phone)}`}
                    className="text-nipost-green hover:text-nipost-green-dark font-medium whitespace-nowrap"
                  >
                    Sign in
                  </Link>
                </div>
              )}

              {!otpSent && (
                <button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={!phoneValid || sendingOtp || phoneTakenError !== ''}
                  className="w-full py-2.5 px-4 rounded-lg bg-nipost-green text-white font-medium text-sm hover:bg-nipost-green-dark transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {sendingOtp ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Checking...
                    </>
                  ) : (
                    'Send OTP'
                  )}
                </button>
              )}

              {otpSent && !phoneVerified && (
                <>
                  <div>
                    <label htmlFor="reg-otp" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Enter 6-digit OTP
                    </label>
                    <input
                      id="reg-otp"
                      type="text"
                      value={otp}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      maxLength={6}
                      className="w-full px-4 py-2.5 rounded-lg border border-border text-sm text-center tracking-[0.3em] font-mono outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green"
                    />
                    <p className="text-xs text-muted mt-1">A verification code was sent to +234{phone}</p>
                    <div className="mt-2">
                      <OtpCountdown seconds={300} onResend={() => { /* mock resend */ }} />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleVerifyOtp}
                    disabled={!otpValid || verifyingOtp}
                    className="w-full py-2.5 px-4 rounded-lg bg-nipost-green text-white font-medium text-sm hover:bg-nipost-green-dark transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {verifyingOtp ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      'Verify'
                    )}
                  </button>
                </>
              )}

              {phoneVerified && (
                <>
                  <div className="flex items-center gap-2 text-green-600 bg-green-50 px-4 py-3 rounded-lg">
                    <CheckCircle2 className="w-5 h-5" />
                    <span className="text-sm font-medium">Phone number verified</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    className="w-full py-2.5 px-4 rounded-lg bg-nipost-green text-white font-medium text-sm hover:bg-nipost-green-dark transition-colors"
                  >
                    Continue
                  </button>
                </>
              )}
              </>
              )}
            </div>
          )}

          {/* Step 2: Personal Details */}
          {step === 2 && (
            <form onSubmit={handleStep2Submit} className="space-y-4" noValidate>
              {submitErrorMessage && (
                <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" role="alert">
                  {submitErrorMessage}
                </div>
              )}

              <div>
                <label htmlFor="reg-name" className="block text-sm font-medium text-gray-700 mb-1.5">Full Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    id="reg-name"
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Adamu Ibrahim"
                    className={`w-full pl-10 pr-4 py-2.5 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green transition-colors ${
                      step2Touched && fullName.trim().length < 2 ? 'border-red-400 bg-red-50/50' : 'border-border'
                    }`}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="reg-email" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    id="reg-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="adamu@example.com"
                    className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-border text-sm outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="reg-password" className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    id="reg-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Min. 12 chars, with A–Z, 0–9, symbol"
                    className={`w-full pl-10 pr-11 py-2.5 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green transition-colors ${
                      step2Touched && !passwordPolicyOk ? 'border-red-400 bg-red-50/50' : 'border-border'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <PasswordPolicyChecklist password={password} />
              </div>

              <div>
                <label htmlFor="reg-confirm" className="block text-sm font-medium text-gray-700 mb-1.5">Confirm Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    id="reg-confirm"
                    type={showConfirm ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter your password"
                    className={`w-full pl-10 pr-11 py-2.5 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green transition-colors ${
                      step2Touched && !passwordsMatch
                        ? 'border-red-400 bg-red-50/50'
                        : confirmPassword.length > 0 && passwordsMatch
                        ? 'border-green-400'
                        : 'border-border'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label={showConfirm ? 'Hide password' : 'Show password'}
                  >
                    {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {step2Touched && !passwordsMatch && confirmPassword.length > 0 && (
                  <p className="text-red-500 text-xs mt-1">Passwords do not match</p>
                )}
              </div>

              <div>
                <span className="block text-sm font-medium text-gray-700 mb-2">Account Type</span>
                <div className="flex gap-4">
                  {(['individual', 'business'] as const).map((type) => (
                    <label
                      key={type}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border cursor-pointer transition-colors flex-1 ${
                        accountType === type
                          ? 'border-nipost-green bg-nipost-green-light text-nipost-green'
                          : 'border-border text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="accountType"
                        value={type}
                        checked={accountType === type}
                        onChange={(e) => setAccountType(e.target.value as 'individual' | 'business')}
                        className="sr-only"
                      />
                      {type === 'individual' ? <User className="w-4 h-4" /> : <Building2 className="w-4 h-4" />}
                      <span className="text-sm font-medium capitalize">{type}</span>
                    </label>
                  ))}
                </div>
              </div>

              {accountType === 'business' && (
                <div className="space-y-4 border-l-2 border-nipost-green-light ml-2 pl-4">
                  <div>
                    <label htmlFor="reg-bizname" className="block text-sm font-medium text-gray-700 mb-1.5">Business Name</label>
                    <input
                      id="reg-bizname"
                      type="text"
                      value={businessName}
                      onChange={(e) => setBusinessName(e.target.value)}
                      placeholder="ABC Logistics Ltd."
                      className={`w-full px-4 py-2.5 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green transition-colors ${
                        step2Touched && !businessName.trim() ? 'border-red-400 bg-red-50/50' : 'border-border'
                      }`}
                    />
                  </div>
                  <div>
                    <label htmlFor="reg-cac" className="block text-sm font-medium text-gray-700 mb-1.5">CAC Registration Number</label>
                    <input
                      id="reg-cac"
                      type="text"
                      value={cacNumber}
                      onChange={(e) => setCacNumber(e.target.value)}
                      placeholder="RC-1234567"
                      className={`w-full px-4 py-2.5 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green transition-colors ${
                        step2Touched && !cacNumber.trim() ? 'border-red-400 bg-red-50/50' : 'border-border'
                      }`}
                    />
                  </div>
                </div>
              )}

              <label className="flex items-start gap-2.5 cursor-pointer pt-1">
                <input
                  type="checkbox"
                  checked={agreedTerms}
                  onChange={(e) => setAgreedTerms(e.target.checked)}
                  className="mt-0.5 w-4 h-4 text-nipost-green border-border rounded focus:ring-nipost-green/20"
                />
                <span className="text-xs text-gray-600 leading-relaxed">
                  I have read and agree to the{' '}
                  <a href="/legal/terms" target="_blank" rel="noreferrer" className="text-nipost-green font-medium hover:underline">
                    Terms &amp; Conditions
                  </a>{' '}
                  and the{' '}
                  <a href="/legal/privacy" target="_blank" rel="noreferrer" className="text-nipost-green font-medium hover:underline">
                    Privacy Policy
                  </a>
                  , including the use of my data for identity verification (NIN/BVN) under the NDPA 2023.
                </span>
              </label>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="px-6 py-2.5 rounded-lg border border-border text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={!step2Valid || registerMutation.isPending}
                  className="flex-1 py-2.5 px-4 rounded-lg bg-nipost-green text-white font-medium text-sm hover:bg-nipost-green-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {registerMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create Account'
                  )}
                </button>
              </div>
            </form>
          )}

          {step === 3 && (
            <div className="text-center py-6">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Account Created</h3>
              <p className="text-muted text-sm mb-1">Your account has been created successfully.</p>
              {registerMutation.data?.customer.customerNumber && (
                <p className="text-sm mb-1">
                  <span className="text-muted">Customer number: </span>
                  <span className="font-mono font-semibold">{registerMutation.data.customer.customerNumber}</span>
                </p>
              )}
              <p className="text-sm mb-6">
                <span className="text-muted">KYC Level: </span>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-nipost-green-light text-nipost-green">
                  {registerMutation.data?.customer.kycLevel ?? 'Basic'}
                </span>
              </p>
              <button
                type="button"
                onClick={() => navigate('/portal/dashboard')}
                className="w-full py-2.5 px-4 rounded-lg bg-nipost-green text-white font-medium text-sm hover:bg-nipost-green-dark transition-colors"
              >
                Go to Dashboard
              </button>
            </div>
          )}
        </div>

        <p className="text-center text-sm text-muted mt-6">
          Already have an account?{' '}
          <Link to="/auth/login" className="text-nipost-green hover:text-nipost-green-dark font-medium">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
